"use client";

import { useState } from "react";
import type { Blackout } from "@/modules/scheduling/surfaces";
import { useFormAction, type FormAction } from "./useFormAction";
import { buttonClass } from "@/design/buttons";
import { Banner } from "./ui";

/**
 * A seat's Blackouts (H40, docs/ux/screens/PractitionerCalendarScreen.md).
 *
 * **The "To" field is the first day back**, and the label says so rather than
 * relying on the reader to know that the range is half-open. That one word is
 * the difference between a week off and a week and a day, and it is the kind
 * of thing that only shows up as a missing appointment.
 */

function hourLabel(hour: number): string {
  return `${String(hour).padStart(2, "0")}:00`;
}

function describe(blackout: Blackout): string {
  const when =
    blackout.startHour === null
      ? "All day"
      : `${hourLabel(blackout.startHour)}–${hourLabel(blackout.endHour!)}`;
  return `${blackout.startDate} → ${blackout.endDate} (back on the ${blackout.endDate}) · ${when}`;
}

export function BlackoutManager({
  practitionerId,
  blackouts,
  today,
  add,
  remove,
}: {
  practitionerId: string;
  blackouts: Blackout[];
  today: string;
  add: FormAction;
  remove: FormAction;
}) {
  const {
    state: addState,
    pending: adding,
    onSubmit: addSubmit,
  } = useFormAction(add);
  const { state: removeState, onSubmit: removeSubmit } = useFormAction(remove);
  const [allDay, setAllDay] = useState(true);

  const fieldError = (name: string) => addState.fieldErrors?.[name];

  return (
    <section className="mt-6 rounded-xl border border-line bg-surface p-5 shadow-sm">
      <h2 className="mb-1 font-bold text-ink">Away</h2>
      <p className="mb-4 text-sm text-muted">
        Time off closes this person&apos;s hours at this clinic only. Booked
        appointments keep their times.
      </p>

      {removeState.formError && <Banner tone="danger">{removeState.formError}</Banner>}
      {addState.formError && <Banner tone="danger">{addState.formError}</Banner>}

      {blackouts.length === 0 ? (
        <p className="mb-5 text-sm text-body">No time off recorded.</p>
      ) : (
        <ul className="mb-5 divide-y divide-line">
          {blackouts.map((blackout) => {
            const past = blackout.endDate <= today;
            return (
              <li
                key={blackout.id}
                className="flex items-start justify-between gap-3 py-3"
              >
                <div className={past ? "text-muted" : "text-ink"}>
                  <p className="text-sm font-semibold">{describe(blackout)}</p>
                  {blackout.reason && (
                    <p className="text-sm text-muted">{blackout.reason}</p>
                  )}
                  {past && <p className="text-xs text-muted">Past</p>}
                </div>
                <form onSubmit={removeSubmit}>
                  <input type="hidden" name="practitionerId" value={practitionerId} />
                  <input type="hidden" name="blackoutId" value={blackout.id} />
                  <button
                    type="submit"
                    className="text-sm font-semibold text-danger hover:underline"
                  >
                    Remove
                  </button>
                </form>
              </li>
            );
          })}
        </ul>
      )}

      <form onSubmit={addSubmit} className="space-y-3 border-t border-line pt-5">
        <input type="hidden" name="practitionerId" value={practitionerId} />
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="font-semibold text-ink">First day away</span>
            <input
              type="date"
              name="startDate"
              defaultValue={addState.values?.startDate}
              className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-ink"
            />
            {fieldError("startDate") && (
              <span className="mt-1 block text-danger">{fieldError("startDate")}</span>
            )}
          </label>
          <label className="block text-sm">
            <span className="font-semibold text-ink">First day back</span>
            <input
              type="date"
              name="endDate"
              defaultValue={addState.values?.endDate}
              className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-ink"
            />
            {fieldError("endDate") && (
              <span className="mt-1 block text-danger">{fieldError("endDate")}</span>
            )}
          </label>
        </div>

        <label className="flex items-center gap-2 text-sm font-semibold text-ink">
          <input
            type="checkbox"
            name="allDay"
            checked={allDay}
            onChange={(e) => setAllDay(e.target.checked)}
            className="size-4"
          />
          All day
        </label>

        {!allDay && (
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="font-semibold text-ink">From</span>
              <select
                name="startHour"
                className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-ink"
              >
                {Array.from({ length: 24 }, (_, hour) => (
                  <option key={hour} value={hour}>
                    {hourLabel(hour)}
                  </option>
                ))}
              </select>
              {fieldError("startHour") && (
                <span className="mt-1 block text-danger">{fieldError("startHour")}</span>
              )}
            </label>
            <label className="block text-sm">
              <span className="font-semibold text-ink">To (first hour back)</span>
              <select
                name="endHour"
                defaultValue={24}
                className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-ink"
              >
                {Array.from({ length: 24 }, (_, i) => i + 1).map((hour) => (
                  <option key={hour} value={hour}>
                    {hourLabel(hour === 24 ? 0 : hour)}
                  </option>
                ))}
              </select>
              {fieldError("endHour") && (
                <span className="mt-1 block text-danger">{fieldError("endHour")}</span>
              )}
            </label>
          </div>
        )}

        <label className="block text-sm">
          <span className="font-semibold text-ink">Reason (optional)</span>
          <input
            type="text"
            name="reason"
            defaultValue={addState.values?.reason}
            className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-ink"
          />
          {fieldError("reason") && (
            <span className="mt-1 block text-danger">{fieldError("reason")}</span>
          )}
        </label>

        <button type="submit" disabled={adding} className={buttonClass("primary")}>
          {adding ? "Saving…" : "Add blackout"}
        </button>
      </form>
    </section>
  );
}
